import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Time picker backed by ngb-timepicker, exposed as a plain 'HH:MM:SS' string.
 *
 * A ControlValueAccessor so `formControlName="time_in"` keeps working unchanged
 * and the FSR form's validators, submit payload and backend TimeField all stay
 * on the same string contract. ngb-timepicker on its own binds to an
 * {hour, minute, second} object, which would have leaked into the form value.
 *
 * ngb-timepicker comes from NgbModule, already imported app-wide — no new dep.
 */
@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select time';
  /** Show the seconds spinner. Off by default — engineers record minutes. */
  @Input() seconds = false;

  time: NgbTimeStruct | null = null;
  disabled = false;

  private onChange: (value: string | null) => void = () => { };
  private onTouched: () => void = () => { };

  // -- ControlValueAccessor -------------------------------------------

  writeValue(value: string | null): void {
    this.time = parseTime(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // -- interaction -----------------------------------------------------

  onTimeChange(value: NgbTimeStruct | null): void {
    this.time = value;
    this.onChange(formatTime(value));
  }

  /** Display value for the readonly trigger input. */
  get display(): string {
    return formatTime(this.time) ?? '';
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.time = null;
    this.onChange(null);
    this.onTouched();
  }

  onClosed(): void {
    this.onTouched();
  }

  setNow(): void {
    const now = new Date();
    this.onTimeChange({
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    });
  }
}

/** 'HH:MM:SS' | 'HH:MM' -> NgbTimeStruct */
export function parseTime(value: string | null): NgbTimeStruct | null {
  if (!value) {
    return null;
  }
  const parts = String(value).split(':').map((part) => Number(part));
  if (parts.length < 2 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }
  const [hour, minute, second = 0] = parts;
  return { hour, minute, second };
}

/** NgbTimeStruct -> 'HH:MM:SS', the shape Django's TimeField expects. */
export function formatTime(value: NgbTimeStruct | null): string | null {
  if (!value || value.hour == null || value.minute == null) {
    return null;
  }
  const pad = (n: number) => String(n ?? 0).padStart(2, '0');
  return `${pad(value.hour)}:${pad(value.minute)}:${pad(value.second ?? 0)}`;
}
